import { inject, provide, ref } from 'vue';
import { getJobConfigById, getLatestJobByCustomerIdAndFlowId } from '../api/insightsApi';
import { getIsOtsResultsStoryReady } from '../api/sumsumApi';
import DDLogs from '../DDLogs';
import { FlowConfigIdType, OtsFlowConfig } from '../types/otsConfig';

export interface OtsJobConfig {
  flowConfigId?: FlowConfigIdType | undefined;
  jobId?: string | undefined;
  [key: string]: string | undefined;
}

interface UseOtsJobConfig {
  getOtsJobConfig: () => Promise<OtsJobConfig | undefined>;
  setOtsJobConfig: (flowConfigId: FlowConfigIdType, jobId?: string) => void;
  getOtsFlowConfig: () => Promise<OtsFlowConfig | undefined>;
  setOtsFlowConfig: (flowConfig: OtsFlowConfig) => void;
  isPolling: boolean,
  startPollingByFlowConfigId: (
    flowConfigId: FlowConfigIdType,
    maxAttempts: number,
    interval: number,
    onSuccess: () => void,
    handleError: () => void
  ) => void;
  updateIsPolling: (value: boolean) => void;
}

const LS_JOB_ID_KEY = 'jobId';
const LS_FLOW_ID_KEY = 'otsFlowConfigId';

const LS_FLOW_CONFIG_KEY = 'otsFlowConfig';

const otsJobConfig = ref<OtsJobConfig | undefined>(undefined);
const otsFlowConfig = ref<OtsFlowConfig | undefined>(undefined);

const isPolling = ref(false);

const updateIsPolling = (value: boolean) => {
  isPolling.value = value;
};

const setOtsJobConfig = (flowConfigId: FlowConfigIdType, jobId: string) => {
  otsJobConfig.value = {
    flowConfigId,
    jobId,
  };
  localStorage.setItem(LS_JOB_ID_KEY, jobId);
  localStorage.setItem(LS_FLOW_ID_KEY, flowConfigId);
};

const fetchLatestJobByFlowId = async (flowId: string): Promise<OtsJobConfig | undefined> => {
  const latestJob = await getLatestJobByCustomerIdAndFlowId(flowId);
  if (latestJob.flowConfigId && latestJob._id) {
    setOtsJobConfig(latestJob.flowConfigId, latestJob._id);
    return { flowConfigId: latestJob.flowConfigId, jobId: latestJob._id };
  }
  return undefined;
};

const setOtsFlowConfig = (flowConfig: OtsFlowConfig) => {
  otsFlowConfig.value = flowConfig;
  localStorage.setItem(LS_FLOW_CONFIG_KEY, JSON.stringify(flowConfig));
};

const getOtsFlowConfig = async (): Promise<OtsFlowConfig | undefined> => {
  if (otsFlowConfig.value) {
    return otsFlowConfig.value;
  }

  const storedFlowConfig = localStorage.getItem(LS_FLOW_CONFIG_KEY);
  if (storedFlowConfig) {
    otsFlowConfig.value = JSON.parse(storedFlowConfig);
    return otsFlowConfig.value;
  }

  return undefined;
};

const getOtsJobConfig = async (): Promise<OtsJobConfig | undefined> => {
  if (otsJobConfig.value) {
    return otsJobConfig.value;
  }

  const storedJobId = localStorage.getItem(LS_JOB_ID_KEY);
  const storedFlowId = localStorage.getItem(LS_FLOW_ID_KEY) as FlowConfigIdType;
  if (storedJobId && storedFlowId) {
    otsJobConfig.value = {
      flowConfigId: storedFlowId,
      jobId: storedJobId,
    };
    return otsJobConfig.value;
  }

  if (storedJobId) {
    const jobConfig = await getJobConfigById(storedJobId);
    otsJobConfig.value = {
      flowConfigId: jobConfig.flowConfigId,
      jobId: storedJobId,
    };
    return otsJobConfig.value;
  }

  return undefined;
};

const startPollingByFlowConfigId = (
  flowConfigId: FlowConfigIdType,
  maxAttempts = 50,
  interval = 3000,
  onSuccess: () => void = () => null,
  handleError: () => void = () => null,
) => {
  let attempts = 0;

  isPolling.value = true;

  const checkOtsResults = async () => {
    attempts++;

    try {
      const isFinishedLoading = await getIsOtsResultsStoryReady(flowConfigId);
      if (!isFinishedLoading) {
        if (attempts >= maxAttempts) {
          stopPolling();
          handleError();
        }
      } else {
        stopPolling();
        onSuccess();
      }
    } catch (error) {
      DDLogs.error('Error checking if ots results story is ready:', error);
      if (attempts >= maxAttempts) {
        stopPolling();
        handleError();
      }
    }
  };

  const stopPolling = () => {
    isPolling.value = false;
    clearInterval(polling);
  };

  const polling = setInterval(checkOtsResults, interval);
  checkOtsResults();
};

export const useOtsJobService = () => {
  provide('setOtsJobConfig', setOtsJobConfig);
  provide('getOtsJobConfig', getOtsJobConfig);
  provide('getOtsFlowConfig', getOtsFlowConfig);
  provide('setOtsFlowConfig', setOtsFlowConfig);
  provide('isPolling', isPolling);
  provide('updateIsPolling', updateIsPolling);
  provide('startPollingByFlowConfigId', startPollingByFlowConfigId);
};

export const useOtsJob = (): UseOtsJobConfig => ({
  setOtsJobConfig: inject('setOtsJobConfig')!,
  getOtsJobConfig: inject('getOtsJobConfig')!,
  getOtsFlowConfig: inject('getOtsFlowConfig')!,
  setOtsFlowConfig: inject('setOtsFlowConfig')!,
  isPolling: inject('isPolling')!,
  startPollingByFlowConfigId: inject('startPollingByFlowConfigId')!,
  updateIsPolling: inject('updateIsPolling')!,
});

// Export functions directly for use outside of Vue component context
export { getOtsFlowConfig, getOtsJobConfig, setOtsFlowConfig, setOtsJobConfig, fetchLatestJobByFlowId };
